import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@src/reducers/store";
import { useI18N } from "@src/language/language-context";
import {
  ActiveInstallationProcessPayload,
  DownloadResult,
  ListItem,
} from "@src/types/ota-update";
import { formatBytes } from "@src/cloud-connect/utils/format-bytes";
import { TimezoneDate } from "@src/components/atoms/timezone-date/timezone-date";
import React from "react";
import { AnyAction } from "redux";
import {
  updateShowConfigurationBackupInstallModal,
  updateShowConfigurationInstallModal,
  updateShowOtaDownloadModal,
  updateShowOtaPushFirmwareBackupModal,
  updateShowOtaPushFirmwareModal,
} from "@src/reducers/your-reducer";
import { downloadFirmwareUseCase } from "@src/cloud-connect/ota-update/use-cases/download-firmware";
import { downloadConfiguratonUseCase } from "@src/cloud-connect/ota-update/use-cases/download-configuration";
import { downloadFirmwareBackupUseCase } from "@src/cloud-connect/ota-update/use-cases/download-firmware-backup";
import { downloadConfigurationBackupUseCase } from "@src/cloud-connect/ota-update/use-cases/download-configuration-backup";
import { postInstallFirmwareUseCase } from "@src/cloud-connect/ota-update/use-cases/post-install-firmware-use-case";
import { postInstallConfigurationUseCase } from "@src/cloud-connect/ota-update/use-cases/post-install-configuration-use-case";
import { postInstallFirmwareBackupUseCase } from "@src/cloud-connect/ota-update/use-cases/post-install-firmware-backup-use-case";
import { postInstallConfigurationBackupUseCase } from "@src/cloud-connect/ota-update/use-cases/post-install-configuration-backup-use-case";
import { deleteFirmwareUseCase } from "@src/cloud-connect/ota-update/use-cases/delete-firmware-use-case";
import { deleteConfigurationUseCase } from "@src/cloud-connect/ota-update/use-cases/delete-configruation-use-case";
import { deleteFirmwareBackupUseCase } from "@src/cloud-connect/ota-update/use-cases/delete-firmware-backup-use-case";
import { deleteConfigurationBackupUseCase } from "@src/cloud-connect/ota-update/use-cases/delete-configuration-backup-use-case";
import {
  errorSnackbar,
  successSnackbar,
} from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import {
  ActiveInstallationStatus,
  FIRMWARE,
  FIRMWARE_TABS,
  OTA_OBJECT_TYPE,
} from "@src/cloud-connect/ota-update/constants";

type InstallResult = {
  success: boolean;
  data?: {
    data?: {
      update_id?: string;
    };
  };
  error?: string;
};

type DeleteResult = {
  success: boolean;
  error?: string;
};

type DownloadFn = (id: string, type: string) => Promise<DownloadResult>;
type InstallFn = (body: {
  firmware_id: string;
  uploader_id: string;
  install_at: string;
  object_type: string;
}) => Promise<InstallResult>;
type DeleteFn = (id: string) => Promise<DeleteResult>;
type ModalAction = (show: boolean) => AnyAction;

const DOWNLOAD_MAP: Record<string, DownloadFn> = {
  FIRMWARE: downloadFirmwareUseCase,
  CONFIGURATION: downloadConfiguratonUseCase,
  FIRMWAREBACKUP: downloadFirmwareBackupUseCase,
  CONFIGURATIONBACKUP: downloadConfigurationBackupUseCase,
};

const INSTALL_MAP: Record<string, InstallFn> = {
  FIRMWARE: postInstallFirmwareUseCase,
  CONFIGURATION: postInstallConfigurationUseCase,
  FIRMWAREBACKUP: postInstallFirmwareBackupUseCase,
  CONFIGURATIONBACKUP: postInstallConfigurationBackupUseCase,
};

const DELETE_MAP: Record<string, DeleteFn> = {
  FIRMWARE: deleteFirmwareUseCase,
  CONFIGURATION: deleteConfigurationUseCase,
  FIRMWAREBACKUP: deleteFirmwareBackupUseCase,
  CONFIGURATIONBACKUP: deleteConfigurationBackupUseCase,
};

const MODAL_ACTION_MAP: Record<string, ModalAction> = {
  FIRMWARE: updateShowOtaPushFirmwareModal,
  FIRMWAREBACKUP: updateShowOtaPushFirmwareBackupModal,
  CONFIGURATION: updateShowConfigurationInstallModal,
  CONFIGURATIONBACKUP: updateShowConfigurationBackupInstallModal,
};

const DELETE_PERMISSION_MAP: Record<string, string> = {
  FIRMWARE: "manage-firmware",
  CONFIGURATION: "manage-configuration",
  FIRMWAREBACKUP: "manage-firmware-backup",
  CONFIGURATIONBACKUP: "manage-configuration-backup",
};

const DOWNLOAD_PERMISSION_MAP: Record<string, string> = {
  FIRMWARE: "manage-firmware",
  CONFIGURATION: "manage-configuration",
  FIRMWAREBACKUP: "manage-firmware-backup",
  CONFIGURATIONBACKUP: "manage-configuration-backup",
};

interface UseOtaModalProps {
  selectedRow: ListItem | null;
  closeFirmwareModal: () => void;
  onInstallStarted?: (updateId: string) => void;
  fetchMainList: () => Promise<void>;
  fetchBackups: () => Promise<void>;
  onSelectPushFirmwareRow?: React.Dispatch<
    React.SetStateAction<ListItem | null>
  >;
  onSelectPushFirmwareBackupRow?: React.Dispatch<
    React.SetStateAction<ListItem | null>
  >;
  activeOTAHistoryTab?: string;
  activeInstallationData?: ActiveInstallationProcessPayload | null;
}

export const useOtaModal = ({
  selectedRow,
  closeFirmwareModal,
  onInstallStarted,
  fetchMainList,
  fetchBackups,
  onSelectPushFirmwareRow,
  onSelectPushFirmwareBackupRow,
  activeOTAHistoryTab,
  activeInstallationData,
}: UseOtaModalProps) => {
  const i18n = useI18N();
  const dispatch = useDispatch();

  const connectionView = useSelector(
    (state: RootState) => state.yourSlice?.connectionView,
  );
  const userId = useSelector(
    (state: RootState) => state.yourSlice.authContext?.userId ?? "",
  );

  const otaActiveView = useSelector(
    (state: RootState) => state.yourSlice.otaActiveView,
  );

  const type = selectedRow?.type;

  const texts = {
    detailsText:
      type === "FIRMWARE" || type === "FIRMWAREBACKUP"
        ? i18n("modules.otaModal.firmwareDetailsText")
        : i18n("modules.otaModal.configurationDetailsText"),
    filename:
      type === "FIRMWARE" || type === "FIRMWAREBACKUP"
        ? i18n("modules.otaHistoryList.firmwareName")
        : i18n("modules.otaHistoryList.configurationName"),
    version: i18n("modules.otaHistoryList.version"),
    digest: i18n("modules.otaHistoryList.digest"),
    size_bytes: i18n("modules.otaHistoryList.size"),
    uploader: i18n("modules.otaHistoryList.uploader"),
    upload_time: i18n("modules.otaHistoryList.uploadTime"),
    noRecords: i18n("modules.otaHistoryList.noRecords"),
    download: i18n("modules.otaUpdateSummary.downloadText"),
    rollbackButtonText: i18n("modules.otaModal.rollbackButtonText"),
    downloadButtonText: i18n("modules.otaModal.downloadButtonText"),
    deleteButtonText: i18n("modules.otaModal.deleteButtonText"),
    failedMsg: i18n("modules.otaModal.failedMsg"),
    errorMsg: i18n("modules.otaModal.errorMsg"),
    deleteSuccess: i18n("modules.otaModal.deleteSuccess"),
    deleteFailed: i18n("modules.otaModal.deleteFailed"),
    errorDelete: i18n("modules.otaModal.errorDelete"),
    pushFirmwareText: i18n("modules.otaModal.pushFirmwareText"),
    pushBackupText: i18n("modules.otaModal.pushBackupText"),
    firmwareAlreadyInstalledTooltip: i18n(
      "modules.otaUpdateSummary.firmwareAlreadyInstalledTooltip",
    ),
    backupAlreadyInstalledTooltip: i18n(
      "modules.otaUpdateSummary.backupAlreadyInstalledTooltip",
    ),
    firmwareInstallationInProgressTooltip: i18n(
      "modules.otaUpdateSummary.firmwareInstallationInProgressTooltip",
    ),
    backupInstallationInProgressTooltip: i18n(
      "modules.otaUpdateSummary.backupInstallationInProgressTooltip",
    ),
    installationFailedCloseErrorFirmwareTooltip: i18n(
      "modules.otaUpdateSummary.installationFailedCloseErrorFirmwareTooltip",
    ),
    installationFailedCloseErrorBackupTooltip: i18n(
      "modules.otaUpdateSummary.installationFailedCloseErrorBackupTooltip",
    ),
  };

  const firmwareFields = [
    { label: texts.filename, value: selectedRow?.filename },
    { label: texts.version, value: selectedRow?.version },
    { label: texts.digest, value: selectedRow?.digest },
    {
      label: texts.size_bytes,
      value: formatBytes(selectedRow?.size_bytes ?? 0),
    },
    { label: texts.uploader, value: selectedRow?.uploader?.display_name },
    {
      label: texts.upload_time,
      value: selectedRow?.uploaded_at
        ? React.createElement(TimezoneDate, { date: selectedRow.uploaded_at })
        : null,
    },
  ];

  const deletePermission =
    DELETE_PERMISSION_MAP[selectedRow?.type ?? ""] ?? null;
  const downloadPermission = selectedRow?.type
    ? DOWNLOAD_PERMISSION_MAP[selectedRow.type]
    : null;

  const isFirmwareAlreadyInstalled =
    selectedRow?.version === connectionView?.source?.current_firmware_version;

  const isInstallationGoingOn = activeInstallationData?.update_id != null;
  const isInstallationFailed =
    activeInstallationData?.status === ActiveInstallationStatus.FAILED;

  const isRollbackDisabled =
    isFirmwareAlreadyInstalled || isInstallationGoingOn;

  const isBackupType = selectedRow?.type === OTA_OBJECT_TYPE.FIRMWARE_BACKUP;

  const versionForTooltip =
    activeInstallationData?.firmware?.version || selectedRow?.version || "";

  let installActionTooltip = "";

  if (isInstallationFailed) {
    installActionTooltip = isBackupType
      ? texts.installationFailedCloseErrorBackupTooltip
      : texts.installationFailedCloseErrorFirmwareTooltip;
  } else if (isInstallationGoingOn) {
    installActionTooltip = (
      isBackupType
        ? texts.backupInstallationInProgressTooltip
        : texts.firmwareInstallationInProgressTooltip
    ).replace("{version}", versionForTooltip);
  } else if (isFirmwareAlreadyInstalled) {
    installActionTooltip = (
      isBackupType
        ? texts.backupAlreadyInstalledTooltip
        : texts.firmwareAlreadyInstalledTooltip
    ).replace("{version}", versionForTooltip);
  }

  const handleDownload = async (id: string, type: string) => {
    dispatch(updateShowOtaDownloadModal(true));

    const apiFn = DOWNLOAD_MAP[type];
    if (!apiFn) {
      console.error("Invalid download type:", type);
      return;
    }

    const result = await apiFn(id, type);
    if (result.success) {
      if (result.handledDirectly) {
        closeFirmwareModal();
        return;
      }

      if (result.data == null || !result.filename) {
        errorSnackbar(texts.errorMsg, 5000);
        return;
      }

      const blob = new Blob([result.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = result.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      closeFirmwareModal();
    } else {
      errorSnackbar(result.error || texts.errorMsg, 5000);
    }
  };

  const handleRollback = async (id: string, type: string) => {
    try {
      const apiFn = INSTALL_MAP[type];
      if (!apiFn) {
        errorSnackbar("Invalid OTA type for installation", 5000);
        return;
      }

      const body = {
        firmware_id: id,
        uploader_id: userId,
        install_at: new Date().toISOString(),
        object_type: type,
      };

      const result = await apiFn(body);

      if (result.success) {
        const updateId = result?.data?.data?.update_id;
        if (!updateId) {
          errorSnackbar("Failed to retrieve update id", 5000);
          return;
        }
        onInstallStarted?.(updateId);
        closeFirmwareModal();
        const modalAction = MODAL_ACTION_MAP[type];
        if (modalAction) dispatch(modalAction(true));
      } else {
        const modalAction = MODAL_ACTION_MAP[type];
        if (modalAction) dispatch(modalAction(false));
        errorSnackbar(result.error || texts.failedMsg, 5000);
      }
    } catch {
      errorSnackbar(texts.errorMsg, 5000);
    }
  };

  const handleNewInstall = () => {
    const selectedRowType = selectedRow?.type;

    if (selectedRowType === OTA_OBJECT_TYPE.FIRMWARE) {
      onSelectPushFirmwareRow?.(selectedRow);
    } else if (selectedRowType === OTA_OBJECT_TYPE.FIRMWARE_BACKUP) {
      onSelectPushFirmwareBackupRow?.(selectedRow);
    }

    if (!selectedRowType) {
      closeFirmwareModal();
      return;
    }

    const modalAction = MODAL_ACTION_MAP[selectedRowType];
    if (modalAction) dispatch(modalAction(true));
    closeFirmwareModal();
  };

  const handleDelete = async (id: string, type: string) => {
    try {
      const apiFn = DELETE_MAP[type];
      if (!apiFn) {
        errorSnackbar("Invalid OTA type for deletion", 5000);
        return;
      }

      const result = await apiFn(id);
      if (result.success) {
        closeFirmwareModal();
        successSnackbar(texts.deleteSuccess, 5000);
        if (type === "FIRMWARE" || type === "CONFIGURATION") {
          fetchMainList();
        } else if (
          type === "FIRMWAREBACKUP" ||
          type === "CONFIGURATIONBACKUP"
        ) {
          fetchBackups();
        }
      } else {
        errorSnackbar(texts.deleteFailed, 5000);
      }
    } catch {
      errorSnackbar(texts.errorDelete, 5000);
    }
  };

  const shouldShowInstallButton =
    otaActiveView === FIRMWARE &&
    activeOTAHistoryTab === FIRMWARE_TABS.VERSIONS;

  return {
    texts,
    firmwareFields,
    deletePermission,
    downloadPermission,
    isRollbackDisabled,
    handleDownload,
    handleRollback,
    handleDelete,
    handleNewInstall,
    installActionTooltip,
    otaActiveView,
    shouldShowInstallButton,
  };
};
